import { Sparkles } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useWorkflowsOptional } from "../context/WorkflowsContext";
import { useOpenWorkflowsTab } from "../hooks/useOpenWorkflowsTab";

/**
 * The Case Files and Work Products toolbars' way to the Case Automations tab;
 * pulses while a workflow runs. It is the toolbar's primary action: in Comfortable density
 * `.prepare-case-button` (styles/explorer-cards.css) fills it with the accent,
 * like a solid "+" button; Compact keeps the quiet ghost icon.
 */
export const WorkflowsSparkle: FC = () => {
  const w = useWorkflowsOptional();
  const tab = useOpenWorkflowsTab();
  if (!w) return null;
  const label = w.running ? "Case Automations (Running)" : "Case Automations";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "prepare-case-button size-6",
            tab.active && "bg-fill-2"
          )}
          aria-label={label}
          aria-pressed={tab.active}
          onClick={tab.openPrepare}
          data-testid="workflows-sparkle"
          data-running={w.running ? "true" : undefined}
        >
          <Sparkles
            className={cn(
              "size-3.5 text-accent-seed",
              w.running && "animate-pulse motion-reduce:animate-none"
            )}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
};

/** Case Automations in the "…" menu, when the toolbar has no room for it. */
export const WorkflowsMenuItem: FC<{ testId?: string }> = ({
  testId = "case-files-more-prepare"
}) => {
  const w = useWorkflowsOptional();
  const tab = useOpenWorkflowsTab();
  if (!w) return null;
  return (
    <DropdownMenuItem onSelect={tab.openPrepare} data-testid={testId}>
      <Sparkles
        className={cn("text-accent-seed", w.running && "animate-pulse")}
      />
      {w.running ? "Case Automations (Running)" : "Case Automations"}
    </DropdownMenuItem>
  );
};
